import { useEffect, useState, useRef, useMemo } from "react";
import Layout from "../components/Layout";
import ResponsiveTable from "../components/ResponsiveTable";
import api, { formatCurrency, formatQty } from "../lib/api";
import {
  Globe,
  Trash2,
  Edit3,
  Upload,
  X,
  Eye,
  EyeOff,
  AlertTriangle,
  ImagePlus,
  Search,
  ExternalLink,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";

/**
 * Website Portal — admin manages exported product listings.
 * Inventory is the master source of truth: price + stock are JOINED live by
 * the backend on every GET, so there's no staleness window or sync button.
 * This page only edits website-OWNED fields: title, marketing copy, seo slug,
 * media gallery, and published state.
 */
export default function Website() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [listings, setListings] = useState([]);
  const [search, setSearch] = useState("");
  const [filterPublished, setFilterPublished] = useState(""); // "", "true", "false"
  const [loading, setLoading] = useState(true);
  const [editingListing, setEditingListing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (filterPublished) params.set("published", filterPublished);
      const { data } = await api.get(`/website/listings?${params}`);
      setListings(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load listings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [search, filterPublished]);

  const onDelete = async (li) => {
    if (!window.confirm(`Remove "${li.website_title}" from the website?\n\nThe source inventory piece stays untouched.`)) return;
    try {
      await api.delete(`/website/listings/${li.id}`);
      toast.success("Removed from website");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const togglePublished = async (li) => {
    try {
      await api.patch(`/website/listings/${li.id}`, { published: !li.published });
      toast.success(li.published ? "Set to draft" : "Now live");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const stats = useMemo(() => {
    const published = listings.filter((l) => l.published).length;
    const drafts = listings.length - published;
    const orphans = listings.filter((l) => l.orphaned).length;
    const totalValue = listings.reduce((s, l) => s + (l.price || 0) * (l.stock || 0), 0);
    return { total: listings.length, published, drafts, orphans, totalValue };
  }, [listings]);

  if (!isAdmin) {
    return (
      <Layout title="Website">
        <div className="text-sm text-red-400">Admin only.</div>
      </Layout>
    );
  }

  return (
    <Layout title="Website Portal" subtitle="Exported listings · live inventory join">
      <Toaster theme="dark" position="top-right" />

      {/* Stat strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatTile label="Listings" value={stats.total} testid="website-stat-total" />
        <StatTile label="Live" value={stats.published} tone="green" testid="website-stat-published" />
        <StatTile label="Drafts" value={stats.drafts} tone="muted" testid="website-stat-drafts" />
        <StatTile
          label="Stock Value"
          value={formatCurrency(stats.totalValue)}
          mono
          testid="website-stat-value"
        />
      </div>

      {stats.orphans > 0 && (
        <div className="mb-5 border border-red-500/30 bg-red-500/[0.04] px-4 py-3 flex items-start gap-3" data-testid="website-orphan-warning">
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" strokeWidth={1.5} />
          <div className="text-xs text-[#EAEAEA]">
            <span className="text-red-300">{stats.orphans} orphaned listing{stats.orphans === 1 ? "" : "s"}</span>
            <span className="text-[#8A8A8A]"> · the source inventory piece was deleted. Remove these listings or re-create the source.</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#666]" strokeWidth={1.5} />
          <input
            data-testid="website-search"
            placeholder="Search title, SKU…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="luxe-input pl-9 pr-4 py-2.5 text-sm w-full sm:w-72"
          />
        </div>
        <select
          data-testid="website-filter-published"
          value={filterPublished}
          onChange={(e) => setFilterPublished(e.target.value)}
          className="luxe-input px-4 py-2.5 text-sm"
        >
          <option value="">All states</option>
          <option value="true">Live only</option>
          <option value="false">Drafts only</option>
        </select>
      </div>

      {/* Table */}
      <ListingsTable
        listings={listings}
        loading={loading}
        onEdit={setEditingListing}
        onDelete={onDelete}
        onTogglePublished={togglePublished}
      />

      {editingListing && (
        <ListingEditorModal
          listing={editingListing}
          onClose={() => setEditingListing(null)}
          onSaved={() => { setEditingListing(null); load(); }}
        />
      )}
    </Layout>
  );
}

function StatTile({ label, value, tone, mono, testid }) {
  const toneClass = tone === "green"
    ? "text-[#7BA585]"
    : tone === "muted"
    ? "text-[#8A8A8A]"
    : "text-[#D4AF37]";
  return (
    <div className="glass-card px-5 py-4" data-testid={testid}>
      <div className="overline mb-2">{label}</div>
      <div className={`serif text-2xl ${mono ? "mono" : ""} ${toneClass}`}>{value}</div>
    </div>
  );
}

function ListingsTable({ listings, loading, onEdit, onDelete, onTogglePublished }) {
  const statePill = (li) => {
    if (li.orphaned) {
      return <span className="px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] border border-red-500/40 text-red-400 bg-red-500/[0.08]">Orphaned</span>;
    }
    if (li.published) {
      return <span className="px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] border border-[#7BA585]/40 text-[#7BA585] bg-[#7BA585]/[0.08]">Live</span>;
    }
    return <span className="px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] border border-[#8A8A8A]/30 text-[#8A8A8A] bg-white/[0.02]">Draft</span>;
  };

  const columns = [
    {
      key: "media",
      label: "",
      width: "70px",
      cell: (li) => {
        const cover = (li.media && li.media[0]) || (li.product_photos && li.product_photos[0]);
        return cover ? (
          <img src={cover} alt="" className="w-12 h-12 object-cover border border-white/10" />
        ) : (
          <div className="w-12 h-12 bg-[#141414] border border-white/5 flex items-center justify-center">
            <ImagePlus className="w-4 h-4 text-[#444]" strokeWidth={1.25} />
          </div>
        );
      },
    },
    {
      key: "title",
      label: "Listing",
      width: "32%",
      cell: (li) => (
        <div className="min-w-0">
          <div className="truncate" data-testid={`website-row-title-${li.product_sku}`}>{li.website_title}</div>
          <div className="text-[0.7rem] text-[#666] mono mt-0.5">{li.product_sku} · {li.product_category}</div>
          <div className="text-[0.65rem] text-[#7BA585] mt-1">
            {li.media?.length || 0} media · slug: <span className="mono">{li.seo_slug}</span>
          </div>
        </div>
      ),
    },
    {
      key: "state",
      label: "State",
      width: "100px",
      cell: statePill,
    },
    {
      key: "stock",
      label: "Live Stock",
      align: "right",
      width: "120px",
      cell: (li) => (
        <span className={`mono text-sm ${li.orphaned ? "text-[#666]" : ""}`}>
          {li.orphaned ? "—" : formatQty(li.stock, li.unit, li.custom_unit)}
        </span>
      ),
    },
    {
      key: "price",
      label: "Live Price",
      align: "right",
      width: "140px",
      cell: (li) => (
        <span className={`mono text-sm ${li.orphaned ? "text-[#666]" : "text-[#D4AF37]"}`}>
          {li.orphaned ? "—" : formatCurrency(li.price)}
        </span>
      ),
    },
    {
      key: "exported",
      label: "Exported",
      align: "right",
      width: "120px",
      hideOnMobile: true,
      cell: (li) => (
        <span className="text-[0.7rem] mono text-[#666]">
          {(li.exported_at || "").slice(0, 10)}
        </span>
      ),
    },
  ];

  const rowActions = (li) => (
    <>
      <button
        onClick={() => onTogglePublished(li)}
        data-testid={`website-publish-${li.product_sku}`}
        disabled={li.orphaned}
        className={`min-h-[44px] sm:min-h-0 p-2 transition-colors inline-flex items-center justify-center ${
          li.orphaned ? "text-[#444] cursor-not-allowed" : li.published ? "text-[#7BA585]" : "text-[#8A8A8A] hover:text-[#7BA585]"
        }`}
        title={li.published ? "Set back to draft" : "Publish (set to live)"}
        aria-label="Toggle published"
      >
        {li.published ? <Eye className="w-4 h-4" strokeWidth={1.25} /> : <EyeOff className="w-4 h-4" strokeWidth={1.25} />}
      </button>
      <button
        onClick={() => onEdit(li)}
        data-testid={`website-edit-${li.product_sku}`}
        className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-[#D4AF37] transition-colors inline-flex items-center justify-center"
        title="Edit listing"
        aria-label="Edit"
      >
        <Edit3 className="w-4 h-4" strokeWidth={1.25} />
      </button>
      <button
        onClick={() => onDelete(li)}
        data-testid={`website-delete-${li.product_sku}`}
        className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-red-400 transition-colors inline-flex items-center justify-center"
        title="Un-export"
        aria-label="Delete"
      >
        <Trash2 className="w-4 h-4" strokeWidth={1.25} />
      </button>
    </>
  );

  return (
    <ResponsiveTable
      columns={columns}
      rows={listings}
      rowKey={(li) => li.id}
      testid="website-listings-table"
      rowActions={rowActions}
      emptyMessage={loading ? "Loading…" : "No website listings yet. Export a piece from Inventory."}
      mobileCard={(li) => ({
        title: <span>{li.website_title}</span>,
        subtitle: <span>{li.product_sku} · {li.product_category}</span>,
        fields: [
          { label: "State", value: statePill(li) },
          { label: "Stock", value: li.orphaned ? "—" : formatQty(li.stock, li.unit, li.custom_unit) },
          { label: "Price", value: <span className="mono text-[#D4AF37]">{li.orphaned ? "—" : formatCurrency(li.price)}</span> },
          { label: "Media", value: `${li.media?.length || 0} item${li.media?.length === 1 ? "" : "s"}` },
        ],
        actions: rowActions(li),
      })}
    />
  );
}

/* ===================================================================== */
/* Listing editor — website-OWNED fields only (title, copy, slug, media)  */
/* Price, stock, and specs are read-only labels pulled live from the      */
/* source product so the admin never accidentally edits master data here. */
/* ===================================================================== */
function ListingEditorModal({ listing, onClose, onSaved }) {
  const [form, setForm] = useState({
    website_title: listing.website_title || "",
    website_description: listing.website_description || "",
    seo_slug: listing.seo_slug || "",
    published: !!listing.published,
  });
  const [media, setMedia] = useState(listing.media || []);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await api.patch(`/website/listings/${listing.id}`, form);
      toast.success("Listing saved");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // Reuse the existing product photo upload endpoint — it just stores a
      // file under gemvault/products/. Website gallery accepts any URL.
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/products/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data: appended } = await api.post(`/website/listings/${listing.id}/media`, { url: data.url });
      setMedia(appended.media);
      toast.success("Media added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeMedia = async (url) => {
    if (!window.confirm("Remove this media item?")) return;
    try {
      const { data } = await api.delete(`/website/listings/${listing.id}/media?url=${encodeURIComponent(url)}`);
      setMedia(data.media);
      toast.success("Media removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-4xl max-h-[92vh] overflow-y-auto fade-up" data-testid="website-editor-modal">
        <button
          onClick={onClose}
          className="absolute right-5 top-5 text-[#8A8A8A] hover:text-[#EAEAEA] z-10"
          aria-label="Close"
          data-testid="website-editor-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-7 py-6 border-b border-white/5">
          <div className="overline mb-1">Website Listing</div>
          <h2 className="serif text-2xl">{listing.website_title}</h2>
          <div className="text-[0.7rem] mono text-[#8A8A8A] mt-1">
            {listing.product_sku} · {listing.product_category} · exported {(listing.exported_at || "").slice(0, 10)}
          </div>
        </div>

        <form onSubmit={save} className="px-7 py-6 space-y-7">
          {/* Inventory-sourced (read-only) — communicates "this is auto" clearly */}
          <section className="border border-[#7BA585]/20 bg-[#7BA585]/[0.03] p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline text-[#7BA585]">Live from Inventory</div>
                <div className="text-[0.7rem] text-[#666] mt-1">Auto-synced on every inventory edit · read-only here</div>
              </div>
              <span className="text-[0.6rem] mono uppercase tracking-[0.18em] text-[#7BA585] px-2 py-1 border border-[#7BA585]/40">
                MASTER
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <ReadOnlyCell label="Stock" value={listing.orphaned ? "—" : formatQty(listing.stock, listing.unit, listing.custom_unit)} testid="editor-stock" />
              <ReadOnlyCell label="Price" value={listing.orphaned ? "—" : formatCurrency(listing.price)} mono testid="editor-price" />
              <ReadOnlyCell label="Warehouse" value={listing.warehouse || "—"} />
              <ReadOnlyCell label="Status" value={listing.inventory_status || "—"} />
              {listing.specs?.carat != null && <ReadOnlyCell label="Carat" value={`${listing.specs.carat} ct`} />}
              {listing.specs?.color && <ReadOnlyCell label="Color" value={listing.specs.color} />}
              {listing.specs?.shape && <ReadOnlyCell label="Shape" value={listing.specs.shape} />}
              {listing.specs?.clarity && <ReadOnlyCell label="Clarity" value={listing.specs.clarity} />}
              {listing.specs?.dimension && <ReadOnlyCell label="Dimension" value={listing.specs.dimension} />}
              {listing.specs?.origin && <ReadOnlyCell label="Origin" value={listing.specs.origin} />}
              {listing.specs?.certification_lab && <ReadOnlyCell label="Certification" value={listing.specs.certification_lab} />}
            </div>
          </section>

          {/* Website-owned fields */}
          <section className="space-y-5">
            <div className="overline">Website copy</div>
            <Field label="Website Title *">
              <input
                required
                value={form.website_title}
                onChange={(e) => setForm({ ...form, website_title: e.target.value })}
                className="luxe-input w-full px-4 py-2.5 text-sm"
                data-testid="editor-title"
                maxLength={200}
              />
            </Field>
            <Field label="SEO Slug">
              <div className="flex items-center gap-2">
                <span className="text-[0.7rem] mono text-[#666] shrink-0">/shop/</span>
                <input
                  value={form.seo_slug}
                  onChange={(e) => setForm({ ...form, seo_slug: e.target.value })}
                  className="luxe-input w-full px-3 py-2 text-sm mono"
                  data-testid="editor-slug"
                  placeholder="my-listing-slug"
                />
              </div>
            </Field>
            <Field label="Description">
              <textarea
                value={form.website_description}
                onChange={(e) => setForm({ ...form, website_description: e.target.value })}
                rows={5}
                className="luxe-input w-full px-4 py-3 text-sm resize-y"
                data-testid="editor-description"
                placeholder="Marketing copy shown on the website…"
              />
            </Field>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                data-testid="editor-published"
                className="w-4 h-4 accent-[#7BA585]"
              />
              <span className="text-sm">
                <span className="text-[#EAEAEA]">Published (live on website)</span>
                <span className="block text-[0.7rem] text-[#666] mt-0.5">Uncheck to revert to draft.</span>
              </span>
            </label>
          </section>

          {/* Media gallery */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">Media Gallery ({media.length}/30)</div>
                <div className="text-[0.7rem] text-[#666] mt-1">Website-exclusive imagery · separate from inventory photos</div>
              </div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || media.length >= 30}
                className="btn-outline-gold px-4 py-2 text-xs uppercase tracking-wider inline-flex items-center gap-2"
                data-testid="editor-upload-media"
              >
                <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                {uploading ? "Uploading…" : "Upload"}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={onUpload}
                className="hidden"
              />
            </div>
            {media.length === 0 ? (
              <div className="border border-dashed border-white/10 px-6 py-10 text-center text-xs text-[#666]" data-testid="editor-media-empty">
                Empty gallery — upload at least one image to feature on the website.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="editor-media-grid">
                {media.map((url, i) => (
                  <div key={url + i} className="relative group aspect-square">
                    <img src={url} alt="" className="w-full h-full object-cover border border-white/10" />
                    <button
                      type="button"
                      onClick={() => removeMedia(url)}
                      className="absolute top-1 right-1 p-1 bg-black/70 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Remove media"
                      data-testid={`editor-media-remove-${i}`}
                    >
                      <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Action bar */}
          <div className="flex flex-col sm:flex-row sm:justify-between gap-3 pt-5 border-t border-white/5">
            <a
              href={`/product/${listing.product_id}`}
              onClick={(e) => e.preventDefault()}
              className="text-xs text-[#8A8A8A] inline-flex items-center gap-1.5"
              title="Public storefront URL will be defined when the public storefront ships"
            >
              <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
              Public URL: <span className="mono ml-1">/shop/{form.seo_slug}</span>
            </a>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
              <button
                type="submit"
                disabled={saving || !form.website_title.trim()}
                className="btn-gold px-5 py-2.5 inline-flex items-center gap-2"
                data-testid="editor-save"
              >
                <Globe className="w-3.5 h-3.5" />
                {saving ? "Saving…" : "Save Listing"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline mb-2">{label}</div>
      {children}
    </div>
  );
}

function ReadOnlyCell({ label, value, mono, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[0.6rem] mono uppercase tracking-[0.18em] text-[#8A8A8A] mb-1">{label}</div>
      <div className={`text-sm text-[#EAEAEA] ${mono ? "mono" : ""}`}>{value}</div>
    </div>
  );
}
